import { getEnvironmentalStudyPDF, getStoredFileDownloadUrl } from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';

/** Opens each URL from its own new-tab link; same-tab navigations would cancel all but the last download. */
function clickDownloadLinks(links: { url: string; name: string }[]) {
  links.forEach(({ url, name }) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

/** Downloads every ERIS PDF for the project, each signed as an attachment so it saves under its display name. */
export async function downloadFullPDF(projectId: string) {
  try {
    const response = await getEnvironmentalStudyPDF(projectId);

    if (!response.files || response.files.length === 0) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.environmentalStudyPDFDownloadFailed, {
        project_id: projectId,
        reason: 'no_files',
      });
      Notify.info({
        title: 'No PDFs Available',
        message: 'There are no environmental study PDFs available for this project yet.',
      });
      return;
    }

    const files: { id: string }[] = response.files;
    const links = await Promise.all(
      files.map(async (file) => {
        const download = await getStoredFileDownloadUrl(projectId, 'eris-pdf', file.id);
        return { url: download.url, name: download.filename };
      }),
    );
    clickDownloadLinks(links);

    captureTypedPosthogEvent(POSTHOG_EVENTS.environmentalStudyPDFDownloaded, {
      project_id: projectId,
      file_count: response.files.length,
    });
  } catch (error) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.environmentalStudyPDFDownloadFailed, {
      project_id: projectId,
      reason: getReasonForError(error),
    });
    notifyDownloadError();
  }
}
